import { useAppDispatch, useAppSelector, useAppTranslation, useErrTranslation } from 'app/hooks';
import BigNumber from 'bignumber.js';
import ConditionButton from 'components/Commons/ConditionButton';
import { showErrorModal, showSuccessModal } from 'components/Modals/CommonModals';
import { PLACE_BET_AUDIO } from 'consts/app';
import Typo from 'consts/typography';

import { useHandleGetWallets } from 'hooks/useHandleGetWallets';
import { useHandleResponseError } from 'hooks/useHandleResponseError';
import useUserDetail from 'hooks/useUserDetail';
import React, { useMemo, useState } from 'react';
import { platformStates } from 'redux/platformSlice';
import VoiceBetButton from '../Futures/VoiceBetButton';
import {
  removeAllTeasers,
  selectLevelPoints,
  selectTeaserPoints,
  selectTeasers,
  selecteCurrentLevel,
} from 'redux/teaserSlice';
import teaserApi from 'api/teaserApi';
import {
  removeAllBets,
  removeAllFutureBets,
  selectCurrentConverter,
} from 'features/home/homeSlice';
import LoadingSpinner from 'components/Commons/LoadingSpinner';
import { useHandleInsufficientBalance } from 'hooks/useHandleInsufficientBalance';

interface BetWrapperButtonFormProps {
  isDisabledButton: boolean;
  teaserAmount: number;
  resetComboAmount: () => void;
}

const aud = new Audio(PLACE_BET_AUDIO);

const TeaserWrapperButtonForm: React.FunctionComponent<BetWrapperButtonFormProps> = ({
  isDisabledButton,
  teaserAmount,
  resetComboAmount,
}) => {
  const t = useAppTranslation();
  const et = useErrTranslation();
  const platform = useAppSelector(platformStates);
  const teasers = useAppSelector(selectTeasers);

  const { isUserLoggedIn, currentUser } = useUserDetail();
  const { getWallet, currentWallet } = useHandleGetWallets();
  const handleResponseError = useHandleResponseError();
  const handleInsufficientBalance = useHandleInsufficientBalance();

  const dispatch = useAppDispatch();
  const currentLevel = useAppSelector(selecteCurrentLevel);
  const levelPoints = useAppSelector(selectLevelPoints);
  const teaserPoints = useAppSelector(selectTeaserPoints);
  const converter = useAppSelector(selectCurrentConverter);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const level = useMemo(() => {
    return levelPoints && levelPoints[currentLevel];
  }, [levelPoints, currentLevel]);

  const teaserPoint = useMemo(() => {
    if (teasers.length < 2 || teasers.length > 6) {
      return null;
    }
    return teaserPoints?.find((t) => t.level.id === level?.id && t.team.value === teasers.length);
  }, [level, teasers.length, teaserPoints]);

  const estimatedWinAmount = useMemo(() => {
    return teaserPoint?.decimalPrice
      ? new BigNumber(teaserPoint.decimalPrice)
          .times(new BigNumber(teaserAmount))
          .minus(new BigNumber(teaserAmount))
      : 0;
  }, [teaserPoint, teaserAmount]);

  const placeBet = async () => {
    setIsLoading(true);
    if (level?.id) {
      const placedBets = teasers.map((t) => ({
        oddsId: t.odd.id,
        gameId: t.gameId,
        market: t.odd.market,
      }));
      const { ok, error } = await teaserApi.placeTeaserBets({
        amount: teaserAmount,
        bets: placedBets,
        pointLevel: level?.value,
      });
      setIsLoading(false);
      if (ok) {
        aud.play();
        getWallet();
        showSuccessModal({
          title: t('bet.success.title'),
          content: t('bet.success.message'),
        });
        resetComboAmount();
        dispatch(removeAllBets());
        dispatch(removeAllTeasers());
        dispatch(removeAllFutureBets());
      }
      if (error) {
        if (error.message === 'error.validate.balance.insufficient') {
          return handleInsufficientBalance();
        }
        if (error?.fieldErrors) {
          showErrorModal({
            content: et(error.fieldErrors.bets[0]),
            title: et(error.title || '', { defaultValue: et('defaultMessage.title') }),
          });
        } else handleResponseError(error);
      }
    }
  };

  const showTeaserValue = (teaserPrice: number) => {
    if (teaserPrice > 0) {
      return `+${teaserPrice}`;
    }
    return teaserPrice;
  };

  return isLoading ? (
    <div className="overlay z-max">
      <LoadingSpinner />
    </div>
  ) : (
    <>
      <div className="bet-wrapper-button">
        <div className="win-cash">
          <span
            className={`primary-text-color ${
              platform.isDesktop || platform.isLargeDesktop ? Typo.p3 : Typo.p5
            }`}
          >
            {t('teaserBet.teaserPrice')}
          </span>
          {converter === 'american' ? (
            <span
              className={`amount ${
                platform.isDesktop || platform.isLargeDesktop ? Typo.p1 : Typo.p3
              }`}
            >
              {`${teaserPoint?.value ? showTeaserValue(teaserPoint.value) : '--'}`}
            </span>
          ) : (
            <span
              className={`amount ${
                platform.isDesktop || platform.isLargeDesktop ? Typo.p1 : Typo.p3
              }`}
            >
              {`${teaserPoint?.value ? teaserPoint.decimalPrice : '--'}`}
            </span>
          )}
        </div>
        <div className="win-cash">
          <span
            className={`primary-text-color ${
              platform.isDesktop || platform.isLargeDesktop ? Typo.p3 : Typo.p5
            }`}
          >
            {t('myBet.possibleWin')}
          </span>
          <span
            className={`amount ${
              platform.isDesktop || platform.isLargeDesktop ? Typo.p1 : Typo.p3
            }`}
          >
            {`${estimatedWinAmount.toFixed(2)} $`}
          </span>
        </div>
        <div className="button-pay">
          <ConditionButton
            onClick={placeBet}
            className={`cursor-pointer button-${
              (isDisabledButton && !isUserLoggedIn) ||
              isLoading ||
              estimatedWinAmount === 0 ||
              teaserAmount === 0
                ? 'disabled'
                : 'secondary'
            } ${platform.isDesktop || platform.isLargeDesktop ? Typo.p1 : Typo.p3}`}
            disabled={
              (isDisabledButton && !isUserLoggedIn) ||
              isLoading ||
              estimatedWinAmount === 0 ||
              teaserAmount === 0
            }
            content={`${t('bet.placeBet')} ${teaserAmount.toFixed(2)} $`}
            testerCheck={true}
          />
        </div>
        <VoiceBetButton />
      </div>
    </>
  );
};

export default TeaserWrapperButtonForm;
